import { useFlags } from "launchdarkly-react-client-sdk";
import { PropsWithChildren, Suspense, lazy } from "react";
import type { LocationGenerics } from "@/router/locationGenerics";
import { useNavigateWithSlug } from "@/router";

import {
  CurrentUserWithAccountsDocument,
  CurrentUserWithAccountsQuery,
  GetRawViewsDocument,
  ListModelFoldersDocument,
  PublishedModelsDocument,
} from "@/apollo/types";
import LoadingSpinner from "@/components/elements/LoadingSpinner";
import Center from "@/components/elements/layout/Center";
import LoadingError from "@/components/elements/routing/LoadingError";
import { AppUnknownExceptionViewRenderer } from "@/components/modules/Error/AppUnknownExceptionView";
import { useMountEffect } from "@/hooks/useMountEffect";
import { useDataWarehouseContext } from "@/providers/DataWarehouseProvider";
import { useToast } from "@/providers/ToastProvider";
import { useCurrentAccount } from "@/providers/account";
import { ApolloClient, DocumentNode } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { Navigate, Route } from "@tanstack/react-location";

/**
 * Everything the Transform tool renders is loaded on demand. Monaco alone is
 * ~2.5MB and used by no other route, so keeping these imports static would put it
 * in the initial bundle for every user.
 */
const importEditorRouteShell = () => import("./EditorRouteShell");
const EditorRouteShell = lazy(importEditorRouteShell);

/**
 * Starts the editor's code and its Firebase sign-in beside the loader's
 * queries; the editor reuses the in-flight sign-in.
 *
 * Skipped for workspaces that have not chosen a transform tool: those are the
 * ones TransformDecisionRedirect bounces to the decision page, and they reach
 * this route from the sidebar. The other redirect it performs is only reachable
 * by deep link, so a wasted prefetch there is acceptable.
 */
function prefetchEditor(
  client: ApolloClient<object>,
  accountId: string | undefined,
) {
  if (!accountId) return;
  const data = client.readQuery<CurrentUserWithAccountsQuery>({
    query: CurrentUserWithAccountsDocument,
  });
  const account = data?.current.accounts?.find((x) => x.id === accountId);
  if (account?.accountSettings.transformTool == null) return;
  // Deliberately not awaited: the point is for them to run beside the queries.
  void importEditorRouteShell();
  void import("@/features/firebase/auth/signInToFirebase").then((m) =>
    m.signInToFirebase(client, accountId),
  );
}

function refreshQuery(
  client: ApolloClient<object>,
  query: DocumentNode,
  accountId: string | undefined,
) {
  return client.query({
    query,
    context: { accountId },
    fetchPolicy: "network-only",
  });
}

export const editorRoute = (
  client: ApolloClient<object>,
): Route<LocationGenerics> => {
  return {
    path: "editor",
    loader: async (_match, { parentMatch }) => {
      if (!parentMatch?.loaderPromise) return {};
      const accountId = (await parentMatch.loaderPromise).accountId;
      prefetchEditor(client, accountId);

      // Nothing here is awaited: the router holds a loader slower than
      // `defaultPendingMs` for a further `defaultPendingMinMs`, and the editor's
      // consumers of these lists wait for them on their own.
      for (const query of [
        GetRawViewsDocument,
        PublishedModelsDocument,
        ListModelFoldersDocument,
      ]) {
        refreshQuery(client, query, accountId).catch(() => {});
      }
      return {};
    },

    // `TransformDecisionRedirect` stays eager on purpose: it redirects users who
    // do not have Transform enabled, and doing that before the lazy shell loads
    // means they never download Monaco.
    element: (
      <Sentry.ErrorBoundary fallback={AppUnknownExceptionViewRenderer}>
        <TransformDecisionRedirect>
          <Suspense fallback={<EditorRouteFallback />}>
            <EditorRouteShell />
          </Suspense>
        </TransformDecisionRedirect>
      </Sentry.ErrorBoundary>
    ),
    children: [
      {
        path: "draft",
        children: [
          {
            path: ":draftId",
            errorElement: <LoadingError />,
            element: () =>
              import("./QueryEditor/DraftEditorRoute").then((m) => (
                <m.default />
              )),
          },
        ],
      },
      {
        path: "dashboard",
        children: [
          {
            path: ":dashboardId",
            errorElement: <LoadingError />,
            element: () =>
              import("./Visualize/VisualizePage").then((m) => <m.default />),
          },
        ],
      },
      {
        path: ":modelId",
        errorElement: <LoadingError />,
        element: () =>
          import("./QueryEditor/ModelLiveEditorRoute").then((m) => (
            <m.default />
          )),
      },
      {
        element: () =>
          import("./components/EditorEmptyState").then((m) => (
            <m.EditorEmptyState />
          )),
      },
    ],
  };
};

function EditorRouteFallback() {
  return (
    <Center className="absolute inset-0 h-full w-full">
      <LoadingSpinner />
    </Center>
  );
}

function TransformDecisionRedirect(props: PropsWithChildren) {
  const account = useCurrentAccount();
  const toast = useToast();
  const { transformTool } = account.accountSettings;
  const navigate = useNavigateWithSlug();

  const { transformDecisionOnboardingEnabled } = useFlags();

  const dwh = useDataWarehouseContext();

  useMountEffect(() => {
    if (!dwh.features.isTransformEnabled) {
      toast(
        "Weld Transform is not enabled",
        "You can enable it in your account settings",
        "warning",
      );
      navigate({
        to: "/",
        replace: true,
      });
    }
  });

  if (transformTool == null && transformDecisionOnboardingEnabled) {
    return <Navigate to={`/${account.slug}/transform_tool_decision`} replace />;
  }

  return <>{props.children}</>;
}
